import {
  Directive,
  inject,
  input,
  ElementRef,
  Renderer2,
  effect,
} from "@angular/core";
import {
  AuthorizationService,
  Permission,
} from "../services/authorization.service";

/**
 * Attribute directive to disable elements based on permissions
 *
 * Usage:
 * ```html
 * <button [appDisableIfNoPermission]="'CREATE_SUBTASK'">Create</button>
 * <input [appDisableIfNoPermission]="'EDIT_SUBTASK'" />
 * ```
 *
 * The element will be disabled if user doesn't have the permission
 * Automatically updates when permissions change
 */
@Directive({
  selector: "[appDisableIfNoPermission]",
  standalone: true,
})
export class DisableIfNoPermissionDirective {
  private readonly _authz = inject(AuthorizationService);
  private readonly _elementRef = inject(ElementRef);
  private readonly _renderer = inject(Renderer2);

  // Use input signal for reactivity
  readonly appDisableIfNoPermission = input.required<Permission>();

  constructor() {
    // Effect runs whenever permission input or user permissions change
    effect(() => {
      const permission = this.appDisableIfNoPermission();
      const hasPermission = this._authz.hasPermission(permission);
      this._updateDisabledState(!hasPermission);
    });
  }

  private _updateDisabledState(disabled: boolean): void {
    const element = this._elementRef.nativeElement;

    if (disabled) {
      this._renderer.setAttribute(element, "disabled", "true");
      this._renderer.addClass(element, "cursor-not-allowed");
      this._renderer.addClass(element, "opacity-50");
    } else {
      this._renderer.removeAttribute(element, "disabled");
      this._renderer.removeClass(element, "cursor-not-allowed");
      this._renderer.removeClass(element, "opacity-50");
    }
  }
}
